import { useState } from "react"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"
import { AuthWrapper } from "@/components/layout"
import { Button } from "@/components/ui/button"
import { Switch } from "@/components/ui/switch"
import { useUpdatePermissions } from "@/features/auth/hooks/useAuth"

/**
 * PermissionSetupScreen - Pixel-perfect implementation matching image copy 6/7.png
 */
export function PermissionSetupScreen() {
  const { t } = useTranslation("onboarding")
  const navigate = useNavigate()
  const updatePermissionsMutation = useUpdatePermissions()

  const [permissions, setPermissions] = useState({
    calendarEnabled: true,
    notificationsEnabled: true,
    contactsEnabled: true,
    locationEnabled: true,
  })

  const isLoading = updatePermissionsMutation.isPending

  const handleSubmit = async () => {
    try {
      await updatePermissionsMutation.mutateAsync(permissions)
      // Navigate to welcome screen on success
      navigate("/welcome")
    } catch (error) {
      // Error is handled by the hook and displayed via toast
      console.error("Permissions update error:", error)
    }
  }

  const toggleItems = [
    {
      key: "calendarEnabled" as const,
      label: t("permissions.calendar"),
      description: t("permissions.calendarDescription"),
    },
    {
      key: "notificationsEnabled" as const,
      label: t("permissions.notifications"),
      description: t("permissions.notificationsDescription"),
    },
    {
      key: "contactsEnabled" as const,
      label: t("permissions.contacts"),
      description: t("permissions.contactsDescription"),
    },
    {
      key: "locationEnabled" as const,
      label: t("permissions.location"),
      description: t("permissions.locationDescription"),
    },
  ]

  return (
    <AuthWrapper maxWidth="660px">
      {/* Title */}
      <div className="text-center mb-8 mt-5">
        <h2 className="text-2xl font-monteserat font-bold text-primary">
          {t("permissions.title")}
        </h2>
      </div>

      {/* Permission Toggles */}
      <div className="space-y-6 mb-8 mt-12 loading-[25%]">
        {toggleItems.map(item => (
          <div key={item.key} className="rounded-lg flex justify-between items-start p-0">
            {/* Text Content - Top */}
            <div className="">
              <h3 className="font-semibold font-monteserat text-[14px] text-primary">
                {item.label}
              </h3>
              <p className="font-monteserat text-[13px] mt-1 text-body">{item.description}</p>
            </div>

            {/* Toggle - Centered */}
            <div className="flex items-center">
              <Switch
                checked={permissions[item.key]}
                onCheckedChange={(checked: boolean) =>
                  setPermissions(prev => ({
                    ...prev,
                    [item.key]: checked,
                  }))
                }
                disabled={isLoading}
              />
            </div>
          </div>
        ))}
      </div>

      {/* Start Your Journey Button */}
      <div className="flex items-center justify-center mt-14">
        <div className="max-w-[250px]">
          <Button
            type="button"
            variant="primary"
            size="md"
            className="py-5 px-30 uppercase"
            isLoading={isLoading}
            fullWidth
            onClick={handleSubmit}
          >
            {t("permissions.enableAll")}
          </Button>
        </div>
      </div>
    </AuthWrapper>
  )
}
